import { Suspense, useEffect, useState } from 'react';

import { IconBrandLinkedin, IconMail, IconPhone } from '@tabler/icons-react';

import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';

import {
  Anchor,
  Box,
  Button,
  Container,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@optiro/ui';

import useResetPassword from '@/hooks/useResetPassword';
import logo from '@/routes/experiences/optiro.svg';

const ResetPasswordContent = () => {
  const search = useSearch({ from: '/reset-password' });
  const navigate = useNavigate();
  const { mutate, isSuccess, isPending } = useResetPassword();

  const [formState, setFormState] = useState({
    key: '',
    password: '',
    passwordConfirm: '',
  });

  useEffect(() => {
    if (search.key) {
      setFormState((prev) => ({ ...prev, key: search.key }));
    }
  }, [search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate(formState);
    navigate({
      to: '/experiences/login',
      search: { notice: 'password-reset-success', returnUrl: undefined },
    });
  };

  if (isSuccess) {
    return (
      <Container size="xs" py="xl">
        <Paper p="xl" radius="md" withBorder shadow="sm" bg="white">
          <Stack gap="md">
            <Title order={2} ta="center" fw={500}>
              Adgangskode nulstillet
            </Title>

            <Text size="sm" c="dimmed" ta="center">
              Din adgangskode er blevet nulstillet. Du kan nu logge ind med din nye adgangskode.
            </Text>

            <Link to="/experiences/login" search={undefined}>
              <Button fullWidth size="lg" style={{ backgroundColor: '#3c735b' }}>
                Gå til login
              </Button>
            </Link>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xs" py="xl">
      <Paper p="xl" radius="md" withBorder shadow="sm" bg="white">
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Title order={2} ta="center" fw={500}>
              Nulstil adgangskode
            </Title>

            <Text size="sm" c="dimmed" ta="center">
              Indtast en ny adgangskode og bekræft den.
            </Text>

            <TextInput
              type="password"
              name="password"
              label="Ny adgangskode"
              placeholder="Skriv ny adgangskode"
              value={formState.password}
              onChange={handleChange}
              size="md"
              required
            />

            <TextInput
              type="password"
              name="passwordConfirm"
              label="Bekræft adgangskode"
              placeholder="Bekræft adgangskode"
              value={formState.passwordConfirm}
              onChange={handleChange}
              size="md"
              required
            />

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={isPending}
              style={{ backgroundColor: '#3c735b' }}
            >
              Nulstil adgangskode
            </Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};

export const ResetPassword = () => {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'rgb(248, 245, 239)' }}>
      {/* Header */}
      <Box
        style={{
          backgroundColor: 'rgb(248, 245, 239)',
        }}
      >
        <Container size="sm">
          <Group justify="center" py="md">
            <Image src={logo} alt="Optiro" h={32} w="auto" />
          </Group>
        </Container>
      </Box>

      <Suspense
        fallback={
          <Container size="xs" py="xl">
            <Paper p="xl" radius="md" withBorder shadow="sm" bg="white">
              <Stack align="center" gap="md">
                <Loader color="#3c735b" />
              </Stack>
            </Paper>
          </Container>
        }
      >
        <ResetPasswordContent />
      </Suspense>

      <Container size="sm">
        <Group justify="center" gap="xl" py="xl">
          <Anchor
            size="sm"
            c="dimmed"
            href="mailto:support@optiro.dk"
            target="_blank"
            style={{ textDecoration: 'none' }}
          >
            <Group gap={6}>
              <IconMail size={16} />
              support@optiro.dk
            </Group>
          </Anchor>
          <Anchor
            size="sm"
            c="dimmed"
            href="tel:+4526122824"
            target="_blank"
            style={{ textDecoration: 'none' }}
          >
            <Group gap={6}>
              <IconPhone size={16} />
              +45 26 12 28 24
            </Group>
          </Anchor>
          <Anchor
            size="sm"
            c="dimmed"
            href="https://linkedin.com/company/optiro-planning"
            target="_blank"
            style={{ textDecoration: 'none' }}
          >
            <Group gap={6}>
              <IconBrandLinkedin size={16} />
              LinkedIn
            </Group>
          </Anchor>
        </Group>
      </Container>
    </div>
  );
};

export const Route = createFileRoute('/reset-password')({
  component: ResetPassword,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      key: typeof search.key === 'string' ? search.key : '',
    };
  },
});
